type Handler<T> = (payload: T) => void;

/**
 * Типизированный pub/sub.
 * - on() возвращает unsubscribe().
 * - Исключения в хендлере не ломают рассылку остальным; падения логируются через console.error.
 */
export class EventBus<M extends Record<string, any>> {
  private readonly handlers = new Map<keyof M, Set<Handler<unknown>>>();

  on<K extends keyof M>(event: K, handler: Handler<M[K]>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as Handler<unknown>);

    return () => this.off(event, handler);
  }

  off<K extends keyof M>(event: K, handler: Handler<M[K]>): void {
    const set = this.handlers.get(event);
    if (!set) return;
    set.delete(handler as Handler<unknown>);
    if (set.size === 0) this.handlers.delete(event);
  }

  emit<K extends keyof M>(event: K, payload: M[K]): void {
    const set = this.handlers.get(event);
    if (!set || set.size === 0) return;

    // Копия чтобы изменения внутри хендлеров (on/off во время emit) не ломали итерацию.
    const snapshot = Array.from(set);
    for (const handler of snapshot) {
      try {
        (handler as Handler<M[K]>)(payload);
      } catch (err) {
        // Изоляция: падение одного хендлера не должно останавливать других подписчиков.
        console.error('[FormaRent] EventBus handler threw:', err);
      }
    }
  }
}
